import { useState } from 'react';

import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { UseSearchPageStateReturn } from '@klareospace/pharos-lib-front-utils';
import type { SortDirection, VehicleStatus, VehicleWithResolvedRefs } from '@klareospace/pharos-lib-types';
import { VEHICLE_STATUS } from '@klareospace/pharos-lib-types';

import type { SearchVehiclesParams } from '../api';
import type { VehicleSortableColumn } from '../types/vehicle.types';
import { useVehicles } from './use-vehicles';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseVehiclesPageOptions {
  carrierId?: string;
  enabled?: boolean;
  siteId?: string;
  spaceId?: string;
}

export interface UseVehiclesPageReturn {
  carrierFilter: string[];
  currentPage: number;
  error: Error | null;
  isLoading: boolean;
  onCarrierFilterChange: (values: string[]) => void;
  onPageChange: (newPage: number) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: VehicleSortableColumn, sortDirection: SortDirection | null) => void;
  onStatusFilterChange: (values: string[]) => void;
  onTemplateFilterChange: (values: string[]) => void;
  pageSize: number;
  searchValue: string;
  sortBy: VehicleSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  statusFilter: VehicleStatus[];
  templateFilter: string[];
  totalItems: number;
  totalPages: number;
  vehicles: VehicleWithResolvedRefs[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const VALID_STATUSES = new Set<string>(Object.values(VEHICLE_STATUS));

function toValidStatuses(values: string[]): VehicleStatus[] {
  return values.filter((v): v is VehicleStatus => VALID_STATUSES.has(v));
}

function toParams(
  searchState: UseSearchPageStateReturn<VehicleSortableColumn>,
  options: UseVehiclesPageOptions,
  carrierFilter: string[],
  statusFilter: VehicleStatus[],
  templateFilter: string[],
): SearchVehiclesParams {
  return {
    carrierId: carrierFilter.length === 1 ? carrierFilter[0] : options.carrierId,
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    search: searchState.debouncedSearch || undefined,
    siteId: options.siteId,
    sort: searchState.sort,
    spaceId: options.spaceId,
    statuses: statusFilter.length > 0 ? statusFilter : undefined,
    vehicleTemplateId: templateFilter.length === 1 ? templateFilter[0] : undefined,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useVehiclesPage(options: UseVehiclesPageOptions = {}): UseVehiclesPageReturn {
  const searchState = useSearchPageState<VehicleSortableColumn>();
  const [carrierFilter, setCarrierFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<VehicleStatus[]>([]);
  const [templateFilter, setTemplateFilter] = useState<string[]>([]);

  const params = toParams(searchState, options, carrierFilter, statusFilter, templateFilter);

  const { data, error, isLoading } = useVehicles({
    enabled: options.enabled,
    ...params,
  });

  function onCarrierFilterChange(values: string[]): void {
    setCarrierFilter(values);
    searchState.onPageChange(1);
  }

  function onStatusFilterChange(values: string[]): void {
    setStatusFilter(toValidStatuses(values));
    searchState.onPageChange(1);
  }

  function onTemplateFilterChange(values: string[]): void {
    setTemplateFilter(values);
    searchState.onPageChange(1);
  }

  return {
    carrierFilter,
    currentPage: searchState.currentPage,
    error: error ?? null,
    isLoading,
    onCarrierFilterChange,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    onStatusFilterChange,
    onTemplateFilterChange,
    pageSize: searchState.pageSize,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    statusFilter,
    templateFilter,
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
    vehicles: data?.items ?? [],
  };
}
